/**
 * bookingQueryKeys — TanStack Query key factory for the booking
 * (reservations) slice (SCRUM-332 epic).
 *
 * Mirrors `propertyQueryKeys` (SCRUM-168 precedent). The factory pattern
 * (vs. inlining `['booking', 'list', filters]` at every call site) gives
 * the slice three things up front:
 *
 *   1. **Single root key.** `bookingQueryKeys.all` is the invalidation
 *      target for "any reservation data" — useful from future mutation
 *      hooks (`useCreateReservation`, `useCancelReservation`,
 *      `useConfirmReservation`, …) that need to fan invalidation across
 *      every list / detail / folio consumer.
 *
 *   2. **Compile-time typo guard.** A misspelled string literal in one
 *      `queryKey: ['bookng', 'list']` invalidates nothing and ships
 *      silently — the factory's `readonly` tuple types catch it at the
 *      typecheck boundary instead.
 *
 *   3. **Migration cost.** When the grid / detail / preview / folio
 *      bodies land (later SCRUM-332 subtasks), the hooks compose against
 *      `bookingQueryKeys.list(filters)` / `.detail(id)` / `.preview(...)`
 *      / `.folio(reservationId)` directly; the SCRUM-333 scaffold commit
 *      only ships the namespace + the list hook, not the rest. No rework
 *      on the consumers.
 *
 * Keys map to the real endpoints (see `packages/contracts/src/openapi.d.ts`):
 *   - lists / list        → `GET /organizations/{slug}/reservations`
 *   - details / detail    → `GET /organizations/{slug}/reservations/{id}`
 *   - preview             → `POST /organizations/{slug}/reservations/preview`
 *                           (stateless quote — keyed by its request shape)
 *   - folio               → `GET /organizations/{slug}/reservations/{reservationId}/folio`
 *   - charges             → `GET /organizations/{slug}/reservations/{id}/charges`
 *   - folioPayments       → `GET /organizations/{slug}/folios/{folioId}/payments`
 */

/**
 * The list filters / pagination shape the `useReservationsList` hook (and
 * eventual grid) consumes. Kept here (not in `types.ts`) so the factory's
 * `.list(filters)` overload is self-contained — `ReservationsListInput`
 * (the richer FE-facing filter type) lives in `./types.js` and is the
 * shape actually passed by the hook; this stub is deliberately permissive
 * (open `Record`-ish) so pagination / future filter keys don't force a
 * factory churn, exactly like `PropertyListFiltersStub`.
 */
export interface BookingListFiltersStub {
  readonly propertyId?: string;
  readonly status?: string | readonly string[];
  readonly from?: string;
  readonly to?: string;
  readonly limit?: number;
  readonly offset?: number;
  /** Pagination cursor some callers pass (barrel-spec shape). */
  readonly page?: number;
}

/**
 * The stateless quote-preview request discriminator. Preview is a POST
 * with a body, not a GET — so the cache entry is keyed by the identifying
 * fields of the prospective stay.
 *
 * SCRUM-337 WIDENED this beyond the original 4-field partial (property +
 * window + `guestCount`) to the FULL preview input shape. The preview
 * breakdown depends on ALL of these axes: `childCount` (a §2.12 StayContext
 * axis frozen into the snapshot), `petCount` / `vehicleCount` (drive
 * per-unit fees), and `reservationType` / `bookingChannel` (select the
 * applicable tax / fee allow-list and the collection model). Keying only on
 * the first four would let two DISTINCT prospective stays that differ solely
 * in an optional axis collide on one cache entry — the second stay would
 * render the first stay's stale quote. The full-shape key keeps every
 * distinct request its own cache entry.
 */
export interface BookingPreviewKeyInput {
  readonly propertyId?: string;
  readonly checkInDate?: string;
  readonly checkOutDate?: string;
  readonly guestCount?: number;
  readonly childCount?: number;
  readonly petCount?: number;
  readonly vehicleCount?: number;
  readonly reservationType?: string | null;
  readonly bookingChannel?: string | null;
}

/**
 * The identifying query args for the availability read
 * (`useReservationAvailability`). Kept permissive/optional so a partially-set
 * form window (before all three are present) still produces a stable,
 * disabled-query cache key without a factory churn — the same permissive
 * posture as `BookingListFiltersStub` / `BookingPreviewKeyInput`.
 */
export interface BookingAvailabilityKeyInput {
  readonly propertyId?: string;
  readonly from?: string;
  readonly to?: string;
}

export const bookingQueryKeys = {
  /** Root namespace — invalidates every reservation query at once. */
  all: ['booking'] as const,

  /** Lists — `bookingQueryKeys.list({ propertyId, status, from, to, … })`. */
  lists: () => [...bookingQueryKeys.all, 'list'] as const,
  list: (filters: BookingListFiltersStub) =>
    [...bookingQueryKeys.lists(), filters] as const,

  /**
   * Reservation availability (SCRUM-350) —
   * `bookingQueryKeys.availability({ propertyId, from, to })`. Rooted at
   * `all` (a per-org EFFECTIVE blocked-nights read for a property + window,
   * not a list/detail), so it invalidates with the booking root but sits
   * alongside `lists`/`types`. Keyed by the identifying query args so each
   * distinct property+window is its own cache entry. Maps to
   * `GET /organizations/{slug}/reservations/availability`.
   */
  availability: (args: BookingAvailabilityKeyInput) =>
    [...bookingQueryKeys.all, 'availability', args] as const,

  /**
   * Reservation-type catalog — `bookingQueryKeys.types()`. Rooted at
   * `all` (a per-org read of the active blueprint types, not a
   * reservation list/detail), so it invalidates with the booking root
   * but sits alongside `lists`/`details` rather than under them.
   */
  types: () => [...bookingQueryKeys.all, 'types'] as const,

  /** Details — `bookingQueryKeys.detail(id)`. */
  details: () => [...bookingQueryKeys.all, 'detail'] as const,
  detail: (id: string) => [...bookingQueryKeys.details(), id] as const,

  /**
   * Stateless charges-breakdown quote — `bookingQueryKeys.preview({ … })`.
   * Rooted at `all` (not under `lists`/`details`) so it invalidates
   * independently: a preview is a prospective stay, not a persisted row.
   */
  preview: (input: BookingPreviewKeyInput) =>
    [...bookingQueryKeys.all, 'preview', input] as const,

  /** Folio for a reservation — `bookingQueryKeys.folio(reservationId)`. */
  folio: (reservationId: string) =>
    [...bookingQueryKeys.detail(reservationId), 'folio'] as const,

  /**
   * Frozen per-line charges for a reservation —
   * `bookingQueryKeys.charges(reservationId)`. Nested under `detail(id)`
   * (beside `folio`) so a detail-scoped invalidation cascades into the
   * ledger's line-item region. Maps to
   * `GET /organizations/{slug}/reservations/{id}/charges`.
   */
  charges: (reservationId: string) =>
    [...bookingQueryKeys.detail(reservationId), 'charges'] as const,

  /**
   * Operator-supplied Channel Reference Notes for a reservation —
   * `bookingQueryKeys.channelNotes(reservationId)`. Nested under `detail(id)`
   * (beside `folio` / `charges` / `history`) so a detail-scoped invalidation
   * cascades into the channel-notes tab. Maps to
   * `GET /organizations/{slug}/reservations/{id}/channel-notes` (SCRUM-382).
   */
  channelNotes: (reservationId: string) =>
    [...bookingQueryKeys.detail(reservationId), 'channel-notes'] as const,

  /** Payment log for a folio — `bookingQueryKeys.folioPayments(folioId)`. */
  folioPayments: (folioId: string) =>
    [...bookingQueryKeys.all, 'folio', folioId, 'payments'] as const,

  /**
   * Entity-change history for a reservation —
   * `bookingQueryKeys.history(reservationId)`. Nested under `detail(id)`
   * (beside `folio` / `charges`) so a detail-scoped invalidation cascades into
   * the history tab. Maps to
   * `GET /organizations/{slug}/reservations/{id}/history` (SCRUM-338).
   */
  history: (reservationId: string) =>
    [...bookingQueryKeys.detail(reservationId), 'history'] as const,
} as const;
